import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Autocomplete,
  Drawer,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Search,
  FilterList,
  Schedule,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService, Location, CustomerLocation as ICustomerLocation, Customer, Order, Item, Team } from '../../services/apiService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Mock data removed - using real orders data

interface LocationField {
  location_id: string;
  address: string;
}

const CustomerDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [customerLocations, setCustomerLocations] = useState<ICustomerLocation[]>([]);
  const [locationFields, setLocationFields] = useState<LocationField[]>([{ location_id: '', address: '' }]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  const [orderFormData, setOrderFormData] = useState({
    item_name: '',
    campaign_name: '',
    voucher_name: '',
    voucher_number: '',
    period: '',
    unit_price: '',
    quantity: '',
    created_by: '',
    creation_date: '',
    coordinated_by: '',
    coordinated_date: '',
    feedback_by: '',
    feedback_date: '',
    effective_start_date: '',
    effective_end_date: '',
    followed_by: '',
    follow_status: '',
    collection_date: '',
    collection_time: '',
    notes: '',
    status: 'pending',
  });
  const [items, setItems] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [orderTeams, setOrderTeams] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [queries, setQueries] = useState<any[]>([]);
  
  // Fetch users on mount for Delivery History display
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersResponse = await apiService.getUsers();
        setUsers(usersResponse.data || []);
      } catch (error: any) {
        console.error('Error fetching users:', error);
      }
    };
    
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchLocations();
    if (id) {
      const customerId = parseInt(id);
      fetchCustomer(customerId);
      fetchCustomerLocations(customerId);
      fetchOrders(customerId);
      fetchQueries(customerId);
    }
  }, [id]);

  const fetchCustomer = async (customerId: number) => {
    try {
      setLoading(true);
      const response = await apiService.getCustomer(customerId);
      setCustomer(response.data);
    } catch (error: any) {
      console.error('Error fetching customer:', error);
      toast.error('Failed to fetch customer details');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async (customerId: number) => {
    try {
      const response = await apiService.getOrders();
      const allOrders = Array.isArray(response.data) ? response.data : [];
      const customerOrders = allOrders
        .filter(order => order.customer_id === customerId)
        .sort((a, b) => b.id - a.id);
      setOrders(customerOrders);
    } catch (error: any) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchQueries = async (customerId: number) => {
    try {
      const response = await apiService.getCallRecords(customerId);
      const queriesData = Array.isArray(response.data) ? response.data : [];
      const sortedQueries = queriesData.sort((a, b) => b.id - a.id);
      setQueries(sortedQueries);
    } catch (error: any) {
      console.error('Error fetching queries:', error);
    }
  };

  const handleOpenOrderDrawer = async (orderToEdit?: Order) => {
    setOrderDrawerOpen(true);
    setEditingOrder(orderToEdit || null);
    
    // Fetch items and users
    try {
      const itemsResponse = await apiService.getItems();
      setItems(itemsResponse.data || []);
      
      const usersResponse = await apiService.getUsers();
      setUsers(usersResponse.data || []);
      
      // Auto-select logged-in user for "Created By" and set current date (only for new orders)
      if (!orderToEdit) {
        const loggedInUser = JSON.parse(localStorage.getItem('user') || 'null');
        const today = new Date().toISOString().split('T')[0];
        
        if (loggedInUser && loggedInUser.name) {
          setOrderFormData(prev => ({
            ...prev,
            created_by: loggedInUser.name,
            creation_date: today
          }));
        } else {
          setOrderFormData(prev => ({
            ...prev,
            creation_date: today
          }));
        }
      } else {
        // Populate form with existing order data
        setOrderFormData({
          item_name: orderToEdit.item_name || '',
          campaign_name: orderToEdit.campaign_name || '',
          voucher_name: orderToEdit.voucher_name || '',
          voucher_number: orderToEdit.voucher_number || '',
          period: orderToEdit.period || '',
          unit_price: orderToEdit.unit_price?.toString() || '',
          quantity: orderToEdit.quantity?.toString() || '',
          created_by: orderToEdit.created_by || '',
          creation_date: orderToEdit.creation_date?.split('T')[0] || '',
          coordinated_by: orderToEdit.coordinated_by || '',
          coordinated_date: orderToEdit.coordinated_date?.split('T')[0] || '',
          feedback_by: orderToEdit.feedback_by || '',
          feedback_date: orderToEdit.feedback_date?.split('T')[0] || '',
          effective_start_date: orderToEdit.effective_start_date?.split('T')[0] || '',
          effective_end_date: orderToEdit.effective_end_date?.split('T')[0] || '',
          followed_by: orderToEdit.followed_by || '',
          follow_status: orderToEdit.follow_status || '',
          collection_date: orderToEdit.collection_date?.split('T')[0] || '',
          collection_time: orderToEdit.collection_time ? orderToEdit.collection_time.substring(0, 5) : '',
          notes: orderToEdit.notes || '',
          status: orderToEdit.status || 'pending',
        });
        
        // Set selected employees if they exist
        if (orderToEdit.assigned_employees) {
          setSelectedEmployees(orderToEdit.assigned_employees);
        }
      }
      
      // Fetch teams
      const teamsResponse = await apiService.getTeams();
      const teamsData = teamsResponse.data || [];
      
      // Find matching teams and track matching locations
      const customerLocationIds = customerLocations.map(l => l.location_id);
      const matchingTeams = teamsData
        .filter((team: Team) => {
          return team.members?.some(member => 
            customerLocationIds.includes(member.location_id)
          );
        })
        .map((team: Team) => {
          // Find which locations match for this team
          const matchingLocationIds = team.members
            ?.filter(member => customerLocationIds.includes(member.location_id))
            .map(member => {
              const location = locations.find(l => l.id === member.location_id);
              return location;
            })
            .filter(Boolean) || [];
          
          return {
            ...team,
            matchingLocations: matchingLocationIds
          };
        });
      setOrderTeams(matchingTeams);
      
      // Auto-select first matching team initially
      if (matchingTeams.length > 0) {
        setSelectedTeam(matchingTeams[0].id);
      }
      
      // Auto-fill period from customer subscription if exists
      if (customer?.subscriptions && customer.subscriptions.length > 0) {
        const subscription = customer.subscriptions.find((s: any) => s.status === 'active');
        if (subscription?.frequency?.name) {
          setOrderFormData(prev => ({
            ...prev,
            period: subscription?.frequency?.name || ''
          }));
        }
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCloseOrderDrawer = () => {
    setOrderDrawerOpen(false);
    setEditingOrder(null);
    setOrderFormData({
      item_name: '',
      campaign_name: '',
      voucher_name: '',
      voucher_number: '',
      period: '',
      unit_price: '',
      quantity: '',
      created_by: '',
      creation_date: '',
      coordinated_by: '',
      coordinated_date: '',
      feedback_by: '',
      feedback_date: '',
      effective_start_date: '',
      effective_end_date: '',
      followed_by: '',
      follow_status: '',
      collection_date: '',
      collection_time: '',
      notes: '',
      status: 'pending',
    });
    setSelectedTeam(null);
    setSelectedEmployees([]);
  };

  const handleOrderEmployeeSelect = (userId: number) => {
    setSelectedEmployees(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleTeamSelect = (teamId: number) => {
    setSelectedTeam(teamId);
    // Clear selected employees when changing teams
    setSelectedEmployees([]);
  };

  const getAllAvailableEmployees = () => {
    const employees: any[] = [];
    // Only show employees from the selected team
    if (selectedTeam) {
      const team = orderTeams.find(t => t.id === selectedTeam);
      if (team?.members) {
        team.members.forEach((member: any) => {
          if (member.user) {
            employees.push({
              id: member.user.id,
              name: member.user.name,
              email: member.user.email,
              teamName: team.name,
              supervisorName: team.supervisor?.name || 'N/A'
            });
          }
        });
      }
    }
    return employees;
  };

  const handleOrderSubmit = async () => {
    if (!id) return;
    
    try {
      // Convert time format from HH:MM to HH:MM:SS
      const collectionTime = orderFormData.collection_time 
        ? `${orderFormData.collection_time}:00` 
        : undefined;
      
      const submitData = {
        ...orderFormData,
        customer_id: parseInt(id),
        assigned_employees: selectedEmployees,
        unit_price: parseFloat(orderFormData.unit_price) || 0,
        quantity: parseFloat(orderFormData.quantity) || 0,
        collection_time: collectionTime,
        status: orderFormData.status as 'pending' | 'in_progress' | 'completed' | 'cancelled',
      };
      
      if (editingOrder) {
        await apiService.updateOrder(editingOrder.id, submitData);
        toast.success('Order updated successfully');
      } else {
        await apiService.createOrder(submitData);
        toast.success('Order created successfully');
      }
      
      handleCloseOrderDrawer();
      fetchOrders(parseInt(id));
    } catch (error: any) {
      toast.error(error.message || `Failed to ${editingOrder ? 'update' : 'create'} order`);
    }
  };

  const handleDeleteOrder = async (orderId: number) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to delete this order!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await apiService.deleteOrder(orderId);
        toast.success('Order deleted successfully');
        if (id) {
          fetchOrders(parseInt(id));
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete order');
      }
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await apiService.getLocations();
      setLocations(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchCustomerLocations = async (customerId: number) => {
    try {
      const response = await apiService.getCustomerLocations(customerId);
      setCustomerLocations(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error('Error fetching customer locations:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddLocationField = () => {
    setLocationFields([...locationFields, { location_id: '', address: '' }]);
  };

  const handleRemoveLocationField = (index: number) => {
    if (locationFields.length > 1) {
      setLocationFields(locationFields.filter((_, i) => i !== index));
    }
  };

  const handleLocationFieldChange = (index: number, field: 'location_id' | 'address', value: string) => {
    const newFields = [...locationFields];
    newFields[index] = { ...newFields[index], [field]: value };
    setLocationFields(newFields);
  };

  const handleOpenDrawer = () => {
    if (customerLocations.length > 0) {
      setLocationFields(
        customerLocations.map(cl => ({
          location_id: cl.location_id.toString(),
          address: cl.address,
        }))
      );
    } else {
      setLocationFields([{ location_id: '', address: '' }]);
    }
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setLocationFields([{ location_id: '', address: '' }]);
  };

  const handleSaveLocations = async () => {
    const hasEmptyFields = locationFields.some(f => !f.location_id || !f.address);
    if (hasEmptyFields) {
      toast.error('Please fill all location fields');
      return;
    }

    if (!id) return;

    setSaving(true);
    try {
      await apiService.bulkSaveCustomerLocations(
        parseInt(id),
        locationFields.map(f => ({
          location_id: parseInt(f.location_id),
          address: f.address,
        }))
      );
      toast.success('Locations saved successfully');
      handleCloseDrawer();
      fetchCustomerLocations(parseInt(id));
    } catch (error: any) {
      console.error('Error saving locations:', error);
      toast.error(error.message || 'Failed to save locations');
    } finally {
      setSaving(false);
    }
  };

  if (!customer) {
  return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: '#f5f7fa',
      minHeight: '100vh', 
      p: 4
    }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          background: 'white',
          borderRadius: '12px',
          p: 3,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <IconButton 
            onClick={() => navigate(-1)} 
            sx={{ 
              mr: 2,
              color: '#6b7280',
              '&:hover': { 
                backgroundColor: '#f9fafb',
                color: '#111827'
              }
            }}
          >
          <ArrowBack />
        </IconButton>
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            color: '#111827',
            letterSpacing: '-0.02em'
          }}>
          Customer Details
        </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<Schedule />}
              onClick={() => handleOpenOrderDrawer()}
              sx={{ 
                px: 3,
                py: 1.2,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                borderColor: '#6366f1',
                color: '#6366f1',
                borderRadius: '8px',
                '&:hover': { 
                  borderColor: '#4f46e5',
                  backgroundColor: '#f0f9ff'
                }
              }}
            >
              Create Order
            </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
              onClick={() => navigate(`/customers/${id}/edit`)}
              sx={{ 
                px: 3,
                py: 1.2,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                backgroundColor: '#6366f1',
                boxShadow: 'none',
                borderRadius: '8px',
                '&:hover': { 
                  backgroundColor: '#4f46e5',
                  boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.3)'
                }
              }}
          >
            Edit Customers
          </Button>
            <IconButton 
              onClick={async () => {
                if (!id) return;
                const result = await Swal.fire({
                  title: 'Are you sure?',
                  text: 'This customer will be deleted. This action cannot be undone!',
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonColor: '#d33',
                  cancelButtonColor: '#3085d6',
                  confirmButtonText: 'Yes, delete it!',
                  cancelButtonText: 'Cancel',
                  reverseButtons: true,
                });

                if (result.isConfirmed) {
                  try {
                    await apiService.deleteCustomer(parseInt(id));
                    toast.success('Customer deleted successfully');
                    navigate('/customers');
                  } catch (error: any) {
                    toast.error(error.message || 'Failed to delete customer');
                  }
                }
              }}
              sx={{ 
                color: '#dc2626',
                '&:hover': { 
                  backgroundColor: '#fef2f2'
                }
              }}
            >
            <Delete />
          </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Customer Info Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Contact Information Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ 
            borderRadius: '12px',
            background: 'white',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            transition: 'all 0.2s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ 
                mb: 2, 
                fontWeight: 700, 
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#6b7280'
              }}>
                Contact Information
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    mr: 2,
                    backgroundColor: '#6366f1',
                    fontSize: '1.5rem',
                    fontWeight: 700
                  }}
                >
                  {customer.name?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: '#111827' }}>
                  {customer.name}
                </Typography>
                  <Chip
                    label={customer.status || 'Active'}
                    size="small"
                    sx={{
                      backgroundColor: customer.status === 'active' ? '#dcfce7' : '#fef2f2',
                      color: customer.status === 'active' ? '#166534' : '#991b1b',
                      fontWeight: 600,
                      height: 22
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ pt: 2, borderTop: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                    Email Address
                </Typography>
                  <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem' }}>
                  {customer.email}
                </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                    Phone Number
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem' }}>
                    {customer.phone || '000 0000 0000'}
                </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Address and Delivery Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ 
            borderRadius: '12px',
            background: 'white',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            transition: 'all 0.2s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ 
                mb: 2, 
                fontWeight: 700, 
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#6b7280'
              }}>
                Address and Delivery
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                  Street Area
                </Typography>
                <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right', maxWidth: '60%' }}>
                  {customer.address || 'Lorem ipsum dolor sit amet, consectetur'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                  City
                </Typography>
                <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>
                  {customer.city || 'Lorem ipsum'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                  Area/Zone
                </Typography>
                <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>
                  {customer.state || 'Lorem ipsum'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                  Delivery Start Date
                </Typography>
                <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>
                  {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '16 Oct, 2025'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                  Delivery Schedule
                </Typography>
                <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>
                  Daily
                </Typography>
              </Box>
              <Button
                variant="contained"
                fullWidth
                startIcon={<Schedule />}
                sx={{ 
                  mt: 1,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  backgroundColor: '#6366f1',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#4f46e5',
                    boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.3)'
                  }
                }}
              >
                Schedule Delivery
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Details Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ 
            borderRadius: '12px',
            background: 'white',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            transition: 'all 0.2s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ 
                mb: 2, 
                fontWeight: 700, 
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#6b7280'
              }}>
                Subscription Details
              </Typography>
              {(() => {
                const activeSubscription = customer?.subscriptions?.find((s: any) => s.status === 'active') || customer?.subscriptions?.[0];
                
                if (!activeSubscription) {
                  return (
                    <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 2 }}>
                      No active subscription
                    </Typography>
                  );
                }
                
                return (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                        Subscription Type
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>
                        {activeSubscription.subscription_type?.display_name || activeSubscription.subscription_type?.name || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                        Package
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>
                        {activeSubscription.name || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                        Frequency
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>
                        {activeSubscription.frequency?.display_name || activeSubscription.frequency?.name || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                        Price
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>
                        AED {activeSubscription.price || '0.00'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                        Start Date
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>
                        {activeSubscription.start_date ? new Date(activeSubscription.start_date).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 600 }}>
                        End Date
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#111827', fontWeight: 600, fontSize: '0.875rem', textAlign: 'right' }}>
                        {activeSubscription.end_date ? new Date(activeSubscription.end_date).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ 
                        mt: 1,
                        py: 1.2,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        borderColor: '#dc2626',
                        color: '#dc2626',
                        borderRadius: '8px',
                        borderWidth: '1px',
                        '&:hover': { 
                          borderColor: '#b91c1c',
                          backgroundColor: '#fef2f2',
                          borderWidth: '1px'
                        }
                      }}
                    >
                      Cancel Subscription
                    </Button>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ 
        borderRadius: '12px',
        background: 'white',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: '#e5e7eb',
            px: 2
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  color: '#6b7280',
                  px: 3,
                  py: 2,
                  minHeight: 48,
                  '&.Mui-selected': {
                    color: '#6366f1',
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#6366f1',
                  height: 2
                }
              }}
            >
              <Tab label="Delivery History" />
              <Tab label="Orders" />
              <Tab label="Customer Locations" />
              <Tab label="Promotions Info" />
              <Tab label="Queries" />
              <Tab label="Distribution Details" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                placeholder="Q Search here"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{ px: 3 }}
              >
                Filter
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                  <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Caller</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Call Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Followed By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No delivery history available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                backgroundColor: order.status === 'completed' ? '#10b981' : 
                                               order.status === 'pending' ? '#f59e0b' : '#6366f1',
                                borderRadius: '50%',
                                mr: 1,
                              }}
                            />
                            {order.teckit_no || 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell>{order.item_name}</TableCell>
                        <TableCell>{order.created_by || 'N/A'}</TableCell>
                        <TableCell>{order.creation_date ? new Date(order.creation_date).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>{order.created_by || 'N/A'}</TableCell>
                        <TableCell>{order.followed_by || 'N/A'}</TableCell>
                        <TableCell>
                          {order.assigned_employees && order.assigned_employees.length > 0 ? (
                            <Box>
                              {users.filter(u => order.assigned_employees?.includes(u.id)).map(emp => emp.name).join(', ')}
                            </Box>
                          ) : (
                            'Not assigned'
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            size="small"
                            sx={{
                              backgroundColor: order.status === 'completed' ? '#e0f2f1' : 
                                            order.status === 'in_progress' ? '#fff7ed' : '#fee2e2',
                              color: order.status === 'completed' ? '#0d9488' : 
                                    order.status === 'in_progress' ? '#f59e0b' : '#dc2626',
                              fontWeight: 500,
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  placeholder="Search orders"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 300 }}
                />
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  sx={{ px: 2 }}
                >
                  Filter
                </Button>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenOrderDrawer()}
                sx={{
                  backgroundColor: '#6366f1',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#4f46e5'
                  }
                }}
              >
                Create New Order
              </Button>
            </Box>

            {orders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No orders found for this customer
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Ticket #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Campaign</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Coordinated By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Feedback By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Followed By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>{order.teckit_no || 'N/A'}</TableCell>
                        <TableCell>{order.item_name}</TableCell>
                        <TableCell>{order.campaign_name || 'N/A'}</TableCell>
                        <TableCell>{order.quantity || 0}</TableCell>
                        <TableCell>AED {order.unit_price || '0.00'}</TableCell>
                        <TableCell>{order.created_by || 'N/A'}</TableCell>
                        <TableCell>{order.coordinated_by || 'N/A'}</TableCell>
                        <TableCell>{order.feedback_by || 'N/A'}</TableCell>
                        <TableCell>{order.followed_by || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            size="small"
                            sx={{
                              backgroundColor: order.status === 'completed' ? '#e0f2f1' : 
                                              order.status === 'in_progress' ? '#fff7ed' : '#fee2e2',
                              color: order.status === 'completed' ? '#0d9488' : 
                                    order.status === 'in_progress' ? '#f59e0b' : '#dc2626',
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenOrderDrawer(order)}
                              sx={{ color: '#6366f1' }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteOrder(order.id)}
                              sx={{ color: '#dc2626' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenDrawer}
                sx={{
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Manage Locations
              </Button>
            </Box>

            {loading ? (
              <Typography>Loading...</Typography>
            ) : customerLocations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No locations added yet
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerLocations.map((location) => (
                      <TableRow key={location.id}>
                        <TableCell>{location.location?.name}</TableCell>
                        <TableCell>{location.address}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={async () => {
                              const result = await Swal.fire({
                                title: 'Are you sure?',
                                text: 'This location will be removed',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#d33',
                                cancelButtonColor: '#3085d6',
                                confirmButtonText: 'Yes, delete it!',
                              });

                              if (result.isConfirmed) {
                                try {
                                  await apiService.deleteCustomerLocation(location.id);
                                  toast.success('Location deleted');
                                  fetchCustomerLocations(parseInt(id || '0'));
                                } catch (error: any) {
                                  toast.error(error.message || 'Failed to delete location');
                                }
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography>Promotions Info content will go here</Typography>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                placeholder="Search queries"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{ px: 2 }}
              >
                Filter
              </Button>
            </Box>

            {queries.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No queries found for this customer
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Call Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queries.map((query) => (
                      <TableRow key={query.id} hover>
                        <TableCell>{query.agent_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={query.call_type}
                            size="small"
                            sx={{
                              backgroundColor: query.call_type === 'inbound' ? '#e0f2f1' : '#fef3c7',
                              color: query.call_type === 'inbound' ? '#0d9488' : '#f59e0b',
                              fontWeight: 500,
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                        <TableCell>{query.duration}</TableCell>
                        <TableCell>
                          {query.timestamp ? new Date(query.timestamp).toLocaleString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={query.status}
                            size="small"
                            sx={{
                              backgroundColor: query.status === 'completed' ? '#e0f2f1' : 
                                              query.status === 'missed' ? '#fee2e2' : '#fef3c7',
                              color: query.status === 'completed' ? '#0d9488' : 
                                    query.status === 'missed' ? '#dc2626' : '#f59e0b',
                              fontWeight: 500,
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {query.rating ? `⭐ ${query.rating}/5` : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {query.notes || 'No notes'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <Typography>Distribution Details content will go here</Typography>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Drawer for managing locations */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 800 },
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1f2937' }}>
            Manage Customer Locations
          </Typography>

          <Paper sx={{ p: 3, backgroundColor: '#f9fafb', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                Customer Locations
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddLocationField}
                size="small"
                sx={{
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  textTransform: 'none',
                }}
              >
                Add Location
              </Button>
            </Box>

            {locationFields.map((field, index) => {
              const selectedLocations = locationFields
                .filter((f, i) => i !== index && f.location_id)
                .map(f => f.location_id);
              
              const availableLocations = locations.filter(loc => 
                !selectedLocations.includes(loc.id.toString())
              );

              return (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Autocomplete
                        options={availableLocations}
                        getOptionLabel={(option) => `${option.name}${option.city ? ` - ${option.city}` : ''}`}
                        value={locations.find(l => l.id.toString() === field.location_id) || null}
                        onChange={(_, newValue) => {
                          handleLocationFieldChange(index, 'location_id', newValue ? newValue.id.toString() : '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Location"
                            required
                          />
                        )}
                        disabled={availableLocations.length === 0}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Address"
                        multiline
                        rows={3}
                        value={field.address}
                        onChange={(e) => handleLocationFieldChange(index, 'address', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                          onClick={() => handleRemoveLocationField(index)}
                          disabled={locationFields.length === 1}
                          sx={{
                            color: '#dc2626',
                            '&:disabled': { color: '#9ca3af' },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Paper>

          {/* Footer Actions */}
          <Box sx={{ display: 'flex', gap: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button
              variant="outlined"
              onClick={handleCloseDrawer}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveLocations}
              disabled={saving}
              sx={{ flex: 1 }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Drawer for creating orders */}
      <Drawer
        anchor="right"
        open={orderDrawerOpen}
        onClose={handleCloseOrderDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 1000 },
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1f2937' }}>
            {editingOrder ? 'Edit Order' : 'Create New Order'}
          </Typography>

          {/* Item Details */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Item Details
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={items}
                getOptionLabel={(item) => `${item.name} - ${item.item_code}`}
                value={items.find(i => i.name === orderFormData.item_name) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setOrderFormData(prev => ({
                      ...prev,
                      item_name: newValue.name,
                      unit_price: newValue.unit_price.toString()
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Item"
                    required
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Unit Price"
                value={orderFormData.unit_price}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={orderFormData.quantity}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, quantity: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={orderFormData.campaign_name}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, campaign_name: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Voucher Name"
                value={orderFormData.voucher_name}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, voucher_name: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Voucher Number"
                value={orderFormData.voucher_number}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, voucher_number: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Period"
                value={orderFormData.period}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, period: e.target.value }))}
                InputProps={{ readOnly: true }}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f5f5f5',
                  }
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Team Selection */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Select Team
          </Typography>
          
          {orderTeams.length > 0 ? (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', mb: 3 }}>
              <List sx={{ p: 0 }}>
                {orderTeams.map((team, index) => (
                  <ListItem
                    key={team.id}
                    sx={{
                      borderBottom: index < orderTeams.length - 1 ? '1px solid #e5e7eb' : 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f9fafb'
                      },
                      backgroundColor: selectedTeam === team.id ? '#f0f9ff' : 'white'
                    }}
                    onClick={() => handleTeamSelect(team.id)}
                  >
                    <Radio
                      checked={selectedTeam === team.id}
                      sx={{ mr: 2 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {team.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                        {team.supervisor && (
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            Supervisor: {team.supervisor.name}
                          </Typography>
                        )}
                        {team.matchingLocations && team.matchingLocations.length > 0 && (
                          <Typography variant="caption" sx={{ color: '#6366f1' }}>
                            Location: {team.matchingLocations.map((loc: any) => loc.name).join(', ')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', mb: 3, p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                No teams available matching customer locations
              </Typography>
            </Box>
          )}
          
          <Divider sx={{ my: 3 }} />

          {/* Employee Selection */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Select Employees to Assign
          </Typography>
          
          {getAllAvailableEmployees().length > 0 ? (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', mb: 3, maxHeight: 400, overflow: 'auto' }}>
              <List sx={{ p: 0 }}>
                {getAllAvailableEmployees().map((employee, index) => (
                  <ListItem
                    key={employee.id}
                    sx={{
                      borderBottom: index < getAllAvailableEmployees().length - 1 ? '1px solid #e5e7eb' : 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f9fafb'
                      },
                      backgroundColor: selectedEmployees.includes(employee.id) ? '#f0f9ff' : 'white'
                    }}
                    onClick={() => handleOrderEmployeeSelect(employee.id)}
                  >
                    <Checkbox
                      checked={selectedEmployees.includes(employee.id)}
                      sx={{ mr: 2 }}
                    />
                    <Box sx={{ flex: 1, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '120px' }}>
                        {employee.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {employee.email}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9ca3af', ml: 'auto' }}>
                        Team: {employee.teamName} | Supervisor: {employee.supervisorName}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', mb: 3, p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {selectedTeam ? 'No employees available in selected team' : 'Please select a team first'}
              </Typography>
            </Box>
          )}
          
          <Divider sx={{ my: 3 }} />

          {/* Creator Details */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Creator Details
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => `${user.name} - ${user.email}`}
                value={users.find(u => u.name === orderFormData.created_by) || null}
                onChange={(event, newValue) => {
                  setOrderFormData(prev => ({ ...prev, created_by: newValue ? newValue.name : '' }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Created By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Creation Date"
                type="date"
                value={orderFormData.creation_date}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, creation_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => `${user.name} - ${user.email}`}
                value={users.find(u => u.name === orderFormData.coordinated_by) || null}
                onChange={(event, newValue) => {
                  setOrderFormData(prev => ({ ...prev, coordinated_by: newValue ? newValue.name : '' }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Coordinated By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Coordinated Date"
                type="date"
                value={orderFormData.coordinated_date}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, coordinated_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => `${user.name} - ${user.email}`}
                value={users.find(u => u.name === orderFormData.feedback_by) || null}
                onChange={(event, newValue) => {
                  setOrderFormData(prev => ({ ...prev, feedback_by: newValue ? newValue.name : '' }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Feedback By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Feedback Date"
                type="date"
                value={orderFormData.feedback_date}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, feedback_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Follow-Up Section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
            Follow-Up
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Effective Start Date"
                type="date"
                value={orderFormData.effective_start_date}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, effective_start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Effective End Date"
                type="date"
                value={orderFormData.effective_end_date}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, effective_end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(user) => `${user.name} - ${user.email}`}
                value={users.find(u => u.name === orderFormData.followed_by) || null}
                onChange={(event, newValue) => {
                  setOrderFormData(prev => ({ ...prev, followed_by: newValue ? newValue.name : '' }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Followed By"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Follow Status"
                value={orderFormData.follow_status}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, follow_status: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Collection Date"
                type="date"
                value={orderFormData.collection_date}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, collection_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Collection Time"
                type="time"
                value={orderFormData.collection_time}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, collection_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={orderFormData.notes}
                onChange={(e) => setOrderFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter notes..."
              />
            </Grid>
          </Grid>

          {/* Footer Actions */}
          <Box sx={{ display: 'flex', gap: 2, pt: 2, borderTop: '1px solid #e5e7eb', position: 'sticky', bottom: 0, backgroundColor: 'white' }}>
            <Button
              variant="outlined"
              onClick={handleCloseOrderDrawer}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleOrderSubmit}
              sx={{ flex: 1, backgroundColor: '#6366f1' }}
            >
              {editingOrder ? 'Update Order' : 'Create Order'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
};

export default CustomerDetails;

